import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MessageCircle, Instagram, BadgeCheck, Loader2, ExternalLink, Send, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getConnections, disconnectChannel, saveOauthSession, simulateInbound } from "@/lib/dentiumApi";
import { loadFacebookSDK, fbLogin } from "@/lib/facebook";

const FB_APP_ID = process.env.REACT_APP_META_APP_ID;
const SCOPES = {
  whatsapp: "whatsapp_business_management,whatsapp_business_messaging",
  instagram: "instagram_basic,instagram_manage_messages,pages_show_list",
};
const CARDS = {
  whatsapp: {
    name: "WhatsApp", icon: MessageCircle, color: "#25D366",
    desc: "Conecta tu cuenta oficial de WhatsApp Business para recibir y responder mensajes automáticamente.",
    label: "Conectar WhatsApp",
  },
  instagram: {
    name: "Instagram", icon: Instagram, color: "#E1306C",
    desc: "Conecta tu cuenta profesional de Instagram para gestionar los mensajes directos desde el panel.",
    label: "Conectar Instagram",
  },
};

export default function ConectarCuentaPage() {
  const qc = useQueryClient();
  const [connecting, setConnecting] = useState(null);
  const [simChannel, setSimChannel] = useState("instagram");
  const [simText, setSimText] = useState("Hola, ¿puedo pedir una cita?");

  const { data: connections = {} } = useQuery({ queryKey: ["connections"], queryFn: getConnections, refetchInterval: 8000 });

  const disconnect = useMutation({
    mutationFn: disconnectChannel,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["connections"] }); toast.success("Cuenta desconectada"); },
  });

  const simulate = useMutation({
    mutationFn: () => simulateInbound({ channel: simChannel, text: simText }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conversations"] });
      toast.success("Mensaje recibido y respondido por el bot", { description: "Míralo en la sección Conversaciones." });
    },
    onError: () => toast.error("No se pudo simular el mensaje entrante"),
  });

  const startOAuth = async (chId) => {
    if (!FB_APP_ID) { toast.error("Falta configurar el App ID de Meta"); return; }
    setConnecting(chId);
    try {
      const FB = await loadFacebookSDK(FB_APP_ID);
      const auth = await fbLogin(FB, SCOPES[chId]);
      await saveOauthSession(chId, { access_token: auth.accessToken, user_id: auth.userID });
      qc.invalidateQueries({ queryKey: ["connections"] });
      toast.success(`${CARDS[chId].name} conectado`, { description: "Cuenta oficial autorizada correctamente a través de Meta." });
    } catch (e) {
      const msg = e?.message === "cancelado"
        ? "Has cancelado la autorización en Meta."
        : e?.message === "sdk_load_error"
        ? "No se pudo cargar el login de Facebook. Revisa tu conexión."
        : "No se pudo completar la conexión con Meta. Verifica que el dominio esté autorizado en la App de Meta.";
      toast.error("No se conectó la cuenta", { description: msg });
    } finally {
      setConnecting(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1000px] mx-auto overflow-y-auto h-full" data-testid="conectar-page">
      <p className="text-sm text-[#8A7E70] text-center mb-8 max-w-xl mx-auto">
        Conecta tus cuentas para que el bot atienda automáticamente. La autorización se realiza siempre en las páginas oficiales de Meta.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {["whatsapp", "instagram"].map((id) => {
          const card = CARDS[id];
          const Icon = card.icon;
          const conn = connections[id];
          const isReal = conn?.mode === "real";
          return (
            <div key={id} data-testid={`connect-card-${id}`} className="rounded-3xl border border-[#E7E2DA] bg-white p-8 flex flex-col items-center text-center">
              <span className="h-20 w-20 rounded-3xl flex items-center justify-center text-white mb-5" style={{ background: card.color }}>
                <Icon className="h-10 w-10" />
              </span>
              <h3 className="text-xl font-semibold text-[#1A1A1A]">{card.name}</h3>
              <p className="text-sm text-[#8A7E70] mt-2 leading-relaxed min-h-[60px]">{card.desc}</p>

              {isReal ? (
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 my-4">
                  <BadgeCheck className="h-5 w-5" /> Cuenta oficial conectada
                </span>
              ) : (
                <span className="text-xs text-[#B9862E] my-4">Todavía no conectada</span>
              )}

              <Button
                data-testid={`btn-connect-account-${id}`}
                onClick={() => startOAuth(id)}
                disabled={connecting === id}
                className="h-14 w-full text-base justify-center text-white hover:opacity-90 disabled:opacity-70 rounded-2xl"
                style={{ background: card.color }}
              >
                {connecting === id ? (
                  <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Abriendo Meta…</>
                ) : (
                  <><ExternalLink className="h-5 w-5 mr-2" /> {isReal ? "Reconectar" : card.label}</>
                )}
              </Button>

              {isReal && (
                <button
                  data-testid={`btn-disconnect-${id}`}
                  onClick={() => disconnect.mutate(id)}
                  className="text-[11px] text-[#A79E90] hover:text-red-500 transition-colors mt-3"
                >
                  Desconectar cuenta
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex items-start gap-2 rounded-xl bg-[#F1EBE1] border border-[#E0D3BC] p-3.5 text-[11px] text-[#6B6157] max-w-xl mx-auto">
        <ShieldCheck className="h-4 w-4 text-[#1A1A1A] shrink-0 mt-0.5" />
        No se solicitan ni almacenan números ni contraseñas en el panel: la autorización se realiza íntegramente en las páginas oficiales de Meta y solo se guarda el permiso cifrado.
      </div>

      {/* Probar respuesta automática del bot */}
      <div className="mt-6 rounded-2xl border border-[#E7E2DA] bg-white p-5 max-w-xl mx-auto" data-testid="simulate-inbound">
        <div className="flex items-center gap-2 mb-3">
          <span className="h-9 w-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center"><Send className="h-4 w-4" /></span>
          <div>
            <h3 className="text-sm font-medium text-[#2A2723]">Probar respuesta automática del bot</h3>
            <p className="text-xs text-[#8A7E70]">Simula un mensaje entrante; el bot responde y aparece en Conversaciones</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            data-testid="sim-channel"
            value={simChannel}
            onChange={(e) => setSimChannel(e.target.value)}
            className="h-10 rounded-md border border-[#E7E2DA] bg-white px-3 text-sm text-[#2A2723]"
          >
            <option value="instagram">Instagram</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
          <Input
            data-testid="sim-text"
            value={simText}
            onChange={(e) => setSimText(e.target.value)}
            placeholder="Escribe un mensaje como si fuera un cliente…"
            className="h-10 text-sm flex-1 border-[#E7E2DA]"
          />
          <Button
            data-testid="sim-send"
            onClick={() => simText.trim() && simulate.mutate()}
            disabled={simulate.isPending || !simText.trim()}
            className="h-10 bg-[#1A1A1A] hover:bg-black text-white disabled:opacity-70"
          >
            {simulate.isPending ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Enviando…</> : <><Send className="h-4 w-4 mr-1.5" /> Simular</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
